import Fuse from 'fuse.js';

// Category slug -> display label, kept in sync with the mapping used across
// the header/footer/category nav components.
const categoryLabels = {
  brackets: 'Brackets',
  bands_and_tubes: 'Bands & Tubes',
  wires_and_springs: 'Wires & Springs',
  elastomerics: 'Elastomerics',
  orthodontic_pliers: 'Orthodontic Pliers',
  miscellaneous: 'Miscellaneous',
};

const formatCategoryLabel = (category) =>
  categoryLabels[category] || (category ? category.replace(/_/g, ' ') : '');

const FUSE_BASE_OPTIONS = {
  includeScore: true,
  // Lets multi-word queries ("band tube") require every word to fuzzy-match
  // somewhere in the record, instead of matching the whole string as one blob.
  useExtendedSearch: true,
  // Typos/partials count the same wherever they land in the word, not just
  // near the position Fuse expects them at — needed for things like a
  // missing middle character ("brakets").
  ignoreLocation: true,
  minMatchCharLength: 2,
};

// Product name carries the most search weight, then category, then
// description. Each field gets its own Fuse index rather than one pooled,
// weighted index: a single blended score dilutes a strong match on one
// field with a poor/no match on the others, which was hiding legitimate
// category-only typo matches (e.g. "brakets" -> Brackets) behind an
// artificially high combined score. Description gets a stricter threshold
// since it's the lowest-signal field and free text is the easiest place for
// a coincidental, irrelevant match to sneak in.
const buildIndexes = (products) => {
  const indexed = products.map((product) => ({
    ...product,
    categoryLabel: formatCategoryLabel(product.category),
  }));
  return {
    byName: new Fuse(indexed, { ...FUSE_BASE_OPTIONS, keys: ['name'], threshold: 0.4 }),
    byCategory: new Fuse(indexed, { ...FUSE_BASE_OPTIONS, keys: ['categoryLabel'], threshold: 0.4 }),
    byDescription: new Fuse(indexed, { ...FUSE_BASE_OPTIONS, keys: ['description'], threshold: 0.3 }),
  };
};

const RESULT_LIMIT = 8;

// Ranking tiers (lower = more relevant), per-tier max score is the guard
// against weak/coincidental matches slipping into results — most visible on
// tier 4 (description), the only field loose enough to occasionally match
// an unrelated product on a multi-word query.
const TIER_MAX_SCORE = { 0: 1, 1: 0.3, 2: 0.5, 3: 0.5, 4: 0.35 };

/**
 * Fuzzy-search `products` for `query`, tolerating typos, missing/extra
 * characters, and partial or multi-word input. Results are ranked:
 *   0. exact product-name match
 *   1. strong product-name fuzzy match
 *   2. partial product-name match
 *   3. category match
 *   4. other fields (description)
 *
 * Pass `category` to scope the search to one category (mirrors the
 * category-page search). Returns plain product objects, same shape as the
 * input list, capped at `limit`.
 */
export const fuzzySearchProducts = (products, query, { category, limit = RESULT_LIMIT } = {}) => {
  const trimmed = (query || '').trim();
  if (!trimmed || !products || products.length === 0) return [];

  const pool = category ? products.filter((item) => item.category === category) : products;
  if (pool.length === 0) return [];

  const { byName, byCategory, byDescription } = buildIndexes(pool);
  const normalizedQuery = trimmed.toLowerCase();
  const bestById = new Map();

  const consider = (item, score, tier) => {
    const existing = bestById.get(item._id);
    if (!existing || tier < existing.tier || (tier === existing.tier && score < existing.score)) {
      bestById.set(item._id, { item, tier, score });
    }
  };

  byName.search(trimmed).forEach(({ item, score = 1 }) => {
    const isExact = item.name?.trim().toLowerCase() === normalizedQuery;
    consider(item, score, isExact ? 0 : score <= 0.2 ? 1 : 2);
  });
  byCategory.search(trimmed).forEach(({ item, score = 1 }) => consider(item, score, 3));
  byDescription.search(trimmed).forEach(({ item, score = 1 }) => consider(item, score, 4));

  const ranked = [...bestById.values()].filter(({ tier, score }) => score <= TIER_MAX_SCORE[tier]);
  ranked.sort((a, b) => a.tier - b.tier || a.score - b.score);

  return ranked.slice(0, limit).map(({ item }) => {
    // Drop the categoryLabel added for indexing so callers get back the
    // original product shape unchanged.
    // eslint-disable-next-line no-unused-vars
    const { categoryLabel, ...product } = item;
    return product;
  });
};
